import { Fragment } from 'react';
import { Building2, CheckCircle2, LogOut, Mail, Phone, CalendarDays } from 'lucide-react';
import { Badge, Button, cn } from '@clarix/ui';
import { useSignOut } from '@clarix/layout';
import { useDelayedLoading } from '@clarix/hooks';
import { useMyDirectoryMember } from '@clarix/services-users';
import type { AccessStatus } from '@clarix/shared-types';
import { useMe } from '@/core/api';
import { TwoFactorManager } from './TwoFactorSection';
import { ConnectedAiAppsSection } from './ConnectedAiAppsSection';
import { IdentityCard } from './IdentityCard';
import { DetailRow, ProfileShell, ProfileSkeleton, SectionCard } from './ProfileSection';
import { ReportsToCard } from './ReportsToCard';
import { roleLine } from './roleLine';
import { WorkDetailsCard } from './WorkDetailsCard';
import { APP_REGISTRY } from '@/gateway/apps';
import { getAccessibleApps } from '@/gateway/accessibleApps';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';

/** Formats an ISO date to a friendly "Jan 5, 2026" label. */
function formatDate(iso?: string): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_VARIANT: Record<AccessStatus, 'default' | 'secondary' | 'destructive'> = {
  ACTIVE: 'default',
  PAUSED: 'secondary',
  SUSPENDED: 'destructive',
};

/**
 * Platform profile page — shared across every app in the workspace. Renders
 * the signed-in user's account details plus their access across all apps.
 * Reached from the avatar at the bottom of the SidebarRail.
 */
export function ProfilePage() {
  const urlSlug = useActiveWorkspaceSlug();
  const me = useMe({ activeSlug: urlSlug ?? undefined });
  const { signOut, signingOut } = useSignOut();
  const myRow = useMyDirectoryMember(me.data?.user?.email ?? undefined);
  const showSkeleton = useDelayedLoading(me.isLoading);

  if (showSkeleton) return <ProfileSkeleton />;

  const user = me.data?.user;
  const appAccess = me.data?.active_workspace_apps ?? {};
  const workspaces = me.data?.workspaces ?? [];
  const activeSlug = me.data?.active_workspace_slug;
  const activeWorkspace = workspaces.find((w) => w.slug === activeSlug);

  const name = user?.name || user?.email?.split('@')[0] || 'User';
  const myMember = myRow.data;

  // "Combination of all the apps" — every registered app, annotated with the
  // user's access in the active workspace. Accessibility uses the shared helper
  // (admin is role-gated; product apps key off the uppercased slug).
  const accessibleIds = new Set(
    getAccessibleApps(me.data, {
      activeSlug: activeSlug ?? undefined,
    }).map((a) => a.id),
  );
  const apps = APP_REGISTRY.map((app) => ({
    id: app.id,
    name: app.name,
    icon: app.icon,
    hasAccess: accessibleIds.has(app.id),
    access: appAccess[app.slug.toUpperCase()],
  }));

  return (
    <ProfileShell
      aside={
        <>
          <IdentityCard
            name={name}
            email={user?.email}
            subtitle={roleLine(myMember?.designation?.name, myMember?.department?.name)}
            badges={activeWorkspace && <Badge variant="secondary">{activeWorkspace.role}</Badge>}
            action={
              <Button
                variant="outline"
                size="sm"
                disabled={signingOut}
                onClick={() => void signOut()}
                className="text-destructive gap-1.5"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
                {signingOut ? 'Signing out…' : 'Sign Out'}
              </Button>
            }
          />
          <ReportsToCard member={myMember} isLoading={myRow.isLoading} />
        </>
      }
      columns={[
        <Fragment key="details">
          {/* Account details */}
          <SectionCard title="Account details">
            <div className="divide-border divide-y">
              <DetailRow icon={Mail} label="Email" value={user?.email ?? '—'} />
              <DetailRow icon={Phone} label="Phone" value={user?.phone || '—'} />
              <DetailRow
                icon={CalendarDays}
                label="Member since"
                value={formatDate(user?.created_at)}
              />
            </div>
          </SectionCard>

          <WorkDetailsCard member={myMember} isLoading={myRow.isLoading} />
        </Fragment>,
        <Fragment key="access">
          {/* Security — two-factor authentication management */}
          <SectionCard title="Security">
            <TwoFactorManager />
          </SectionCard>

          {/* App access — combination of all apps */}
          <SectionCard title="App access">
            <ul className="divide-border divide-y">
              {apps.map((app) => {
                const Icon = app.icon;
                return (
                  <li key={app.id} className="flex items-center gap-3 py-2.5">
                    <span
                      className={cn(
                        'flex h-8 w-8 flex-none items-center justify-center rounded-md',
                        app.hasAccess
                          ? 'bg-primary/10 text-primary'
                          : 'bg-surface text-muted-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-xl font-medium">{app.name}</p>
                      {app.access && (
                        <p className="text-muted-foreground truncate text-lg capitalize">
                          {app.access.role.toLowerCase()}
                        </p>
                      )}
                    </div>
                    {app.access ? (
                      <Badge variant={STATUS_VARIANT[app.access.status]} className="gap-1">
                        {app.access.status === 'ACTIVE' && (
                          <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
                        )}
                        {app.access.status}
                      </Badge>
                    ) : app.hasAccess ? (
                      <Badge variant="default" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
                        Access
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        No access
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          {/* Workspaces */}
          <SectionCard title="Workspaces">
            <ul className="divide-border divide-y">
              {workspaces.map((ws) => (
                <li key={ws.id} className="flex items-center gap-3 py-2.5">
                  <span className="text-muted-foreground bg-surface flex h-8 w-8 flex-none items-center justify-center rounded-md">
                    <Building2 className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-xl font-medium">{ws.name}</p>
                    <p className="text-muted-foreground truncate text-lg">{ws.slug}</p>
                  </div>
                  <div className="flex flex-none items-center gap-1.5">
                    {ws.slug === activeSlug && (
                      <Badge variant="default" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
                        Current
                      </Badge>
                    )}
                    <Badge variant="secondary">{ws.role}</Badge>
                  </div>
                </li>
              ))}
              {workspaces.length === 0 && (
                <li className="text-muted-foreground py-2.5 text-xl">No workspaces yet.</li>
              )}
            </ul>
          </SectionCard>
        </Fragment>,
      ]}
      footer={
        // Full width: the URL, the connection rows and the setup snippets all need the room.
        <SectionCard title="Connected AI apps">
          <ConnectedAiAppsSection />
        </SectionCard>
      }
    />
  );
}
